<template>
  <div class="settings-card">
    <div class="settings-card__header issuers-header">
      <div>
        <div class="settings-card__title">{{ $t('configurations.issuers.title') }}</div>
        <div class="settings-card__subtitle">
          {{ $t('configurations.issuers.subtitle', { assetClass: assetClassName }) }}
        </div>
      </div>
      <b-button variant="primary" data-test="issuers-new" @click="openCreate">
        {{ $t('configurations.issuers.new') }}
      </b-button>
    </div>

    <b-alert :show="!!listError" variant="danger" class="mb-3" data-test="issuers-list-error">
      {{ listError ? $t(listError) : '' }}
    </b-alert>

    <b-table
      v-if="loaded"
      :items="issuers"
      :fields="fields"
      :tbody-tr-class="rowClass"
      show-empty
      small
      responsive
      class="issuers-table"
      data-test="issuers-table"
    >
      <template #empty>
        <div class="issuers-empty">{{ $t('configurations.issuers.empty') }}</div>
      </template>

      <template #cell(issuerCode)="row">
        <span class="issuers-code">{{ row.item.issuerCode }}</span>
      </template>

      <template #cell(issuerName)="row">
        <span class="issuers-name">{{ row.item.issuerName }}</span>
      </template>

      <template #cell(ivassCode)="row">
        <span class="issuers-code">{{ row.item.ivassCode }}</span>
      </template>

      <template #cell(aliases)="row">
        <span v-for="alias in row.item.aliases" :key="alias" class="issuers-tag issuers-tag--alias">{{ alias }}</span>
      </template>

      <template #cell(enabled)="row">
        <span class="issuers-tag" :class="row.item.enabled ? 'issuers-tag--success' : 'issuers-tag--neutral'">
          {{ row.item.enabled ? $t('configurations.issuers.enabled') : $t('configurations.issuers.disabled') }}
        </span>
      </template>

      <template #cell(actions)="row">
        <div class="issuers-actions">
          <b-button size="sm" variant="outline-secondary" data-test="issuers-edit" @click="openEdit(row.item)">
            {{ $t('configurations.issuers.edit') }}
          </b-button>
          <b-button size="sm" variant="outline-secondary" data-test="issuers-toggle" @click="toggleEnabled(row.item)">
            {{ row.item.enabled ? $t('configurations.issuers.disable') : $t('configurations.issuers.enable') }}
          </b-button>
        </div>
      </template>
    </b-table>

    <b-modal
      v-model="showModal"
      :title="isCreate ? $t('configurations.issuers.newTitle') : $t('configurations.issuers.editTitle')"
      :ok-title="$t('configurations.issuers.save')"
      :cancel-title="$t('configurations.issuers.cancel')"
      :ok-disabled="saving"
      size="lg"
      @ok="onOk"
    >
      <b-alert :show="!!formError" variant="danger" data-test="issuers-form-error">
        {{ formError ? $t(formError) : '' }}
      </b-alert>

      <b-form-group :label="$t('configurations.issuers.fields.issuerCode')" label-for="issuer-code">
        <b-form-input
          id="issuer-code"
          v-model="form.issuerCode"
          :disabled="!isCreate"
          maxlength="255"
          data-test="issuers-code"
        />
        <small v-if="!isCreate" class="issuers-help">{{ $t('configurations.issuers.codeLocked') }}</small>
      </b-form-group>

      <b-form-group :label="$t('configurations.issuers.fields.issuerName')" label-for="issuer-name">
        <b-form-input id="issuer-name" v-model="form.issuerName" maxlength="255" data-test="issuers-name" />
      </b-form-group>

      <b-form-group :label="$t('configurations.issuers.fields.issuerNameFfk')" label-for="issuer-ffk">
        <b-form-input id="issuer-ffk" v-model="form.issuerNameFfk" maxlength="255" data-test="issuers-ffk" />
      </b-form-group>

      <b-form-group :label="$t('configurations.issuers.fields.ivassCode')" label-for="issuer-ivass">
        <b-form-input id="issuer-ivass" v-model="form.ivassCode" maxlength="100" data-test="issuers-ivass" />
      </b-form-group>

      <b-form-group :label="$t('configurations.issuers.fields.aliases')" label-for="issuer-aliases">
        <b-form-tags
          id="issuer-aliases"
          v-model="form.aliases"
          :tag-validator="isValidAlias"
          :placeholder="$t('configurations.issuers.aliasesPlaceholder')"
          add-button-variant="outline-primary"
          data-test="issuers-aliases"
        />
        <small class="issuers-help">{{ $t('configurations.issuers.aliasesHelp') }}</small>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import { fetchCustomMethod } from '../../utils/fetcher'
import { describeIssuerError } from '../../utils/issuerErrors'

const EMPTY_FORM = () => ({ issuerCode: '', issuerName: '', issuerNameFfk: '', ivassCode: '', aliases: [] })

export default {
  name: 'Issuers',
  data() {
    return {
      loaded: false,
      issuers: [],
      listError: null,
      showModal: false,
      isCreate: true,
      saving: false,
      formError: null,
      form: EMPTY_FORM(),
    }
  },
  computed: {
    assetClassName() {
      const slug = this.$store.getters.getSelectedAssetClass
      return this.$t(`navbar.assetClass.${slug}.name`)
    },
    fields() {
      return [
        { key: 'issuerCode', label: this.$t('configurations.issuers.fields.issuerCode') },
        { key: 'issuerName', label: this.$t('configurations.issuers.fields.issuerName') },
        { key: 'issuerNameFfk', label: this.$t('configurations.issuers.fields.issuerNameFfk') },
        { key: 'aliases', label: this.$t('configurations.issuers.fields.aliases') },
        { key: 'ivassCode', label: this.$t('configurations.issuers.fields.ivassCode') },
        { key: 'enabled', label: this.$t('configurations.issuers.fields.status') },
        { key: 'actions', label: '' },
      ]
    },
  },
  methods: {
    // The separator of the aliases in the database: the server refuses it, so it is not accepted here either.
    isValidAlias(alias) {
      return alias.trim().length > 0 && !alias.includes('|')
    },
    rowClass(item) {
      return item && !item.enabled ? 'issuers-row--disabled' : ''
    },
    load() {
      this.listError = null
      return this.get('/api/issuers', {})
        .then((resp) => {
          this.issuers = Array.isArray(resp) ? resp : []
          this.loaded = true
        })
        .catch((err) => {
          this.loaded = true
          return describeIssuerError(err).then((rejection) => {
            this.listError = rejection.messageKey
          })
        })
    },
    openCreate() {
      this.isCreate = true
      this.form = EMPTY_FORM()
      this.formError = null
      this.showModal = true
    },
    openEdit(issuer) {
      this.isCreate = false
      this.form = {
        issuerCode: issuer.issuerCode,
        issuerName: issuer.issuerName || '',
        issuerNameFfk: issuer.issuerNameFfk || '',
        ivassCode: issuer.ivassCode || '',
        aliases: [...(issuer.aliases || [])],
      }
      this.formError = null
      this.showModal = true
    },
    // Keeps the modal open until the server has accepted the data: a refusal is shown in it.
    onOk(event) {
      event.preventDefault()
      return this.save()
    },
    save() {
      this.saving = true
      this.formError = null

      const body = {
        issuerName: this.form.issuerName,
        issuerNameFfk: this.form.issuerNameFfk,
        ivassCode: this.form.ivassCode,
        aliases: this.form.aliases,
      }
      const request = this.isCreate
        ? this.post('/api/issuers', { ...body, issuerCode: this.form.issuerCode })
        : fetchCustomMethod(`/api/issuers/${encodeURIComponent(this.form.issuerCode)}`, 'PUT', body)

      return request
        .then(() => {
          this.showModal = false
          return this.load()
        })
        .catch((err) => this.showError(err, true))
        .finally(() => {
          this.saving = false
        })
    },
    toggleEnabled(issuer) {
      return fetchCustomMethod(`/api/issuers/${encodeURIComponent(issuer.issuerCode)}/enabled`, 'PUT', {
        enabled: !issuer.enabled,
      })
        .then(() => this.load())
        .catch((err) => this.showError(err, false))
    },
    // A refusal of the form is shown in the form; one of a row action in the list. When the issuer is gone the
    // list is stale: it is read again.
    showError(err, inForm) {
      return describeIssuerError(err).then((rejection) => {
        if (rejection.stale) {
          // The reload clears the list message: the refusal is told after it, in the list, and the form closes.
          return this.load().then(() => {
            this.showModal = false
            this.listError = rejection.messageKey
          })
        }
        if (inForm) {
          this.formError = rejection.messageKey
        } else {
          this.listError = rejection.messageKey
        }
      })
    },
  },
  created() {
    return this.load()
  },
}
</script>

<style lang="scss" scoped>
// Fairmat design system tokens (colors_and_type.css): the app is Vue 2 + bootstrap-vue, so the PrimeVue components
// are not usable here; the page follows the system's tokens, type scale and patterns.
.settings-card {
  --fm-text-default: #0f172a;
  --fm-text-muted: #64748b;
  --fm-border: #e2e8f0;
  --fm-surface-50: #f8fafc;
  --fm-surface-100: #f1f5f9;
  --fm-font-mono: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;

  background: #fff;
  border: 1px solid var(--fm-border);
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  color: var(--fm-text-default);
  font-size: 14px;
  line-height: 20px;
}
.settings-card__header {
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--fm-border);
}
.issuers-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
// H2 / section title: 16/24 · 600
.settings-card__title {
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: var(--fm-text-default);
}
.settings-card__subtitle {
  margin-top: 4px;
  font-size: 14px;
  line-height: 20px;
  color: var(--fm-text-muted);
}
.issuers-table {
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 0;
  color: var(--fm-text-default);

  // DataTable density: cells 8×12, header 12/16 · 600 on a slate-50 band.
  /deep/ th {
    padding: 8px 12px;
    background: var(--fm-surface-50);
    border-top: 0;
    border-bottom: 1px solid var(--fm-border);
    font-size: 12px;
    line-height: 16px;
    font-weight: 600;
    color: var(--fm-text-muted);
    vertical-align: middle;
  }
  /deep/ td {
    padding: 8px 12px;
    border-top: 0;
    border-bottom: 1px solid var(--fm-border);
    vertical-align: middle;
  }
  /deep/ tbody tr:hover td {
    background: var(--fm-surface-50);
  }
}
/deep/ .issuers-row--disabled td {
  color: var(--fm-text-muted);
}
// Machine values read in the table: JetBrains Mono 13px, no background.
.issuers-code {
  font-family: var(--fm-font-mono);
  font-size: 13px;
  line-height: 20px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.issuers-name {
  font-weight: 500;
}
// Tag: 20px, 12/16 · 600, text always present (status is never colour only).
.issuers-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  border: 1px solid transparent;
  &--success {
    background: #f0fdf4;
    border-color: #bbf7d0;
    color: #166534;
  }
  &--neutral {
    background: var(--fm-surface-100);
    border-color: var(--fm-border);
    color: #475569;
  }
  &--alias {
    margin-right: 4px;
    font-weight: 500;
    background: var(--fm-surface-100);
    border-color: var(--fm-border);
    color: #334155;
  }
}
.issuers-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  white-space: nowrap;
}
// Empty state lives inside the table: neutral, title naming the cause.
.issuers-empty {
  padding: 24px 0;
  text-align: center;
  font-weight: 600;
  color: var(--fm-text-muted);
}
// Help text under a field: 12/16, muted.
.issuers-help {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  line-height: 16px;
  color: var(--fm-text-muted);
}
</style>
